import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tables } from "@/integrations/supabase/types";

type PrintServiceProvider = Tables<"print_service_providers">;

const formSchema = z.object({
  providerId: z.string().min(1, "Print provider is required"),
  prefix: z.string(),
  startNumber: z.string().min(1, "Start number is required"),
  endNumber: z.string().min(1, "End number is required"),
  digitCount: z.string().min(1, "Number of digits is required"),
  suffix: z.string(),
});

export type GeneratorForm = z.infer<typeof formSchema>;

interface Props {
  providers?: PrintServiceProvider[];
  onSubmit: (values: GeneratorForm) => void;
  isLoading?: boolean;
}

export function HologramGeneratorForm({ providers, onSubmit, isLoading }: Props) {
  const form = useForm<GeneratorForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      providerId: "",
      prefix: "",
      startNumber: "1",
      endNumber: "100",
      digitCount: "4",
      suffix: "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="providerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Print Provider</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {providers?.map((provider) => (
                    <SelectItem
                      key={provider.provider_id}
                      value={provider.provider_id.toString()}
                    >
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="prefix"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prefix</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter prefix" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min="1"
                    placeholder="Start number"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min="1"
                    placeholder="End number"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="digitCount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Number of Digits</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min="1"
                  max="10"
                  placeholder="Enter number of digits"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="suffix"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Suffix</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter suffix" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Generating..." : "Generate Preview"}
          </Button>
        </div>
      </form>
    </Form>
  );
}